import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import React, { useEffect, useState } from "react";

import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { useDebounce } from "@shared/hooks/de-boucing";
import { toast } from "@shared/hooks/use-toast";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Edit, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { AddEditForm, FormData } from "../../../../shared/addEditForm";
import {
  useConditionParameters,
  useCreateConditionParameter,
  useDeleteConditionParameter,
  useUpdateConditionParameter,
} from "../../hooks";
import { ConditionParameter } from "../../types";

export const ParametersList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedParameter, setSelectedParameter] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [parameterToDelete, setParameterToDelete] =
    useState<ConditionParameter | null>(null);
  const [editingParameter, setEditingParameter] =
    useState<ConditionParameter | null>(null);
  const [expandedAccordionItem, setExpandedAccordionItem] =
    useState<string>("");

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // API hooks - get all data when no search term, search data when there is a search term
  const { data: allParametersResponse } = useConditionParameters("");
  const { data: searchParametersResponse } = useConditionParameters(
    debouncedSearchTerm && searchTerm ? debouncedSearchTerm : ""
  );

  const createParameterMutation = useCreateConditionParameter();
  const updateParameterMutation = useUpdateConditionParameter();
  const deleteParameterMutation = useDeleteConditionParameter();

  // Use search results if search term exists, otherwise use all data
  const currentApiResponse = searchTerm
    ? searchParametersResponse
    : allParametersResponse;
  const parameters = currentApiResponse?.parameters || [];

  // Color coding for weightage validation
  const getWeightageColor = (weightage: number) => {
    if (Math.abs(weightage - 100) < 0.1) return "text-green-600";
    return "text-red-600";
  };

  const handleAccordionChange = (value: string) => {
    setExpandedAccordionItem(value);
    setSelectedParameter(value);
  };

  // Event handlers
  const handleAddClick = () => {
    logEvent("condition_parameter_add_clicked");
    setEditingParameter(null);
    setShowDialog(true);
  };

  const handleEditClick = (parameter: ConditionParameter) => {
    logEvent("condition_parameter_edit_clicked", {
      id: parameter.id,
      name: parameter.name,
    });
    setEditingParameter(parameter);
    setShowDialog(true);
  };

  const handleDeleteClick = (parameter: ConditionParameter) => {
    logEvent("condition_parameter_delete_clicked", {
      id: parameter.id,
      name: parameter.name,
    });
    setParameterToDelete(parameter);
    setShowDeleteDialog(true);
  };

  // Updated delete function for confirmation
  const handleConfirmDelete = async () => {
    if (!parameterToDelete) return;
    logEvent("condition_parameter_delete_confirmed", {
      id: parameterToDelete.id,
      name: parameterToDelete.name,
    });

    try {
      await deleteParameterMutation.mutateAsync({
        code: parameterToDelete.code,
        name: parameterToDelete.name,
      });

      // Close dialog on success
      setShowDeleteDialog(false);
      setParameterToDelete(null);
    } catch (error) {
      toast({
        title: `${error.message}`,
        variant: "destructive",
      });
    }
  };

  // Function to close delete dialog
  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setParameterToDelete(null);
  };

  const handleFormSubmit = async (data: FormData) => {
    const operation = editingParameter ? "update" : "create";

    try {
      switch (operation) {
        case "update":
          const updatePayload = {
            code: editingParameter.code,
            name: data.name,
            weightScore: data.weightScore,
            description: data.description,
          };
          await updateParameterMutation.mutateAsync(updatePayload);
          break;

        case "create":
          const createPayload = {
            name: data.name,
            weightScore: data.weightScore,
            description: data.description,
          };
          await createParameterMutation.mutateAsync(createPayload);
          break;

        default:
          throw new Error("Invalid operation");
      }
    } catch (error) {
      toast({
        title: `${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditingParameter(null);
  };

  // Handle successful mutation to close dialog
  useEffect(() => {
    if (
      createParameterMutation.isSuccess ||
      updateParameterMutation.isSuccess
    ) {
      setShowDialog(false);
      setEditingParameter(null);
      createParameterMutation.reset();
      updateParameterMutation.reset();
    }
  }, [createParameterMutation.isSuccess, updateParameterMutation.isSuccess]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between gap-4 mt-5">
        <div>
          <Input
            id="search"
            placeholder="Search condition parameters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-96"
          />
        </div>
        <Button onClick={handleAddClick} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Condition Parameter
        </Button>
      </div>

      <Card>
        {parameters && parameters.length > 0 ? (
          <Accordion
            type="single"
            collapsible
            className="w-full"
            value={expandedAccordionItem}
            onValueChange={handleAccordionChange}
          >
            {parameters?.map((parameter) => (
              <AccordionItem key={parameter?.id} value={parameter?.id}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex flex-col items-start gap-2 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-blue-600">
                          {parameter?.name}
                        </span>
                        <Badge variant="secondary">
                          {parameter?.weightage}%
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Score: {parameter?.weightScore}
                        </Badge>
                      </div>
                      {parameter?.description && (
                        <div className="text-left w-full">
                          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                            <span className="font-medium text-gray-800">
                              Description:{" "}
                            </span>
                            {parameter.description}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(parameter);
                        }}
                        disabled={updateParameterMutation.isPending}
                      >
                        {updateParameterMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Edit className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(parameter);
                        }}
                        disabled={deleteParameterMutation.isPending}
                      >
                        {deleteParameterMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    {/* Parameter Details */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">
                            Weight Score:
                          </span>
                          <p className="text-gray-600">
                            {parameter.weightScore}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            Weightage:
                          </span>
                          <p
                            className={`${getWeightageColor(
                              parameter.weightage
                            )}`}
                          >
                            {parameter.weightage}%
                          </p>
                        </div>
                        {parameter.createdBy && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Created by:
                            </span>
                            <p className="text-gray-600">
                              {parameter.createdBy}
                            </p>
                          </div>
                        )}
                        {parameter.createdDate && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Created:
                            </span>
                            <p className="text-gray-600">
                              {parameter.createdDate}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <CardContent className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-gray-900">
                  No Condition Parameters Available
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  {searchTerm
                    ? `No condition parameters found matching "${searchTerm}". Try adjusting your search or create a new condition parameter.`
                    : "Get started by creating your first condition parameter to monitor key environmental conditions."}
                </p>
              </div>
              <Button
                onClick={handleAddClick}
                className="flex items-center gap-2"
                disabled={createParameterMutation.isPending}
              >
                {createParameterMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create Condition Parameter
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <AddEditForm
        isOpen={showDialog}
        onClose={handleDialogClose}
        onSubmit={handleFormSubmit}
        initialData={
          editingParameter
            ? {
                id: editingParameter.id,
                name: editingParameter.name,
                description: editingParameter.description || "",
                weightScore: editingParameter.weightScore,
              }
            : undefined
        }
        title={
          editingParameter
            ? "Edit Condition Parameter"
            : "Add New Condition Parameter"
        }
        submitButtonText={
          editingParameter ? "Update Parameter" : "Add Parameter"
        }
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete Condition Parameter"
        itemName={parameterToDelete?.name}
        description={`Are you sure you want to delete the condition parameter "${parameterToDelete?.name}"? This action cannot be undone.`}
        isLoading={deleteParameterMutation.isPending}
      />
    </div>
  );
};
